import { useEffect, useState } from "react";
import { Link } from "@/components/LocalizedLink";
import { isAppMode } from "@/lib/app-mode";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "alkoholprovet_cookie_consent_v1";

type Choice = "granted" | "denied";

function applyConsent(choice: Choice) {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag(..._args: unknown[]) {
    w.dataLayer.push(arguments);
  }
  w.gtag = w.gtag || gtag;
  w.gtag("consent", "update", {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
}

// Sidor där dialogen aldrig får visas: den ligger fast i nederkant och
// täckte "Betala"-knappen i Stripes betalformulär på mobil – en direkt
// konverteringsblockerare. Consent ligger kvar på default-deny tills
// besökaren väljer på någon annan sida.
const SUPPRESSED_PATHS = ["/checkout", "/success", "/en/checkout", "/en/success"];

function isSuppressedPath(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/+$/, "");
  return SUPPRESSED_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

export function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // App-mode (Expo WebView): visa ingen cookie-dialog och applicera aldrig
    // ett tidigare "granted"-val här. Consent Mode ligger kvar på default-deny
    // (satt i __root.tsx) så inga Ads-cookies sätts. Sparat webbval lämnas
    // orört så vanliga browserbesök behåller sitt beteende.
    if (isAppMode()) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Choice | null;
      if (!stored) {
        if (!isSuppressedPath()) setVisible(true);
      } else {
        applyConsent(stored);
      }
    } catch {
      if (!isSuppressedPath()) setVisible(true);
    }
  }, []);


  const decide = (choice: Choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* ignore */
    }
    applyConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("aCookie.ariaLabel")}
      className="fixed inset-x-3 bottom-3 z-[60] md:inset-x-auto md:right-4 md:bottom-4 md:max-w-md"
    >
      <div className="rounded-2xl border border-border bg-background/95 backdrop-blur shadow-2xl p-5">
        <p className="font-display text-base font-semibold">{t("aCookie.title")}</p>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          {t("aCookie.body")}
          <Link to="/integritetspolicy" className="underline">
            {t("aCookie.privacyPolicy")}
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            {t("aCookie.onlyNecessary")}
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {t("aCookie.acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
